import { UseCases } from "@aptus/frontend-core";
import { event } from "event";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEvent } from "utils/useEvent";
import { Routes } from "./models/routes";

interface Result {
	routes: Routes;
	currentPathname: string;
	activeRegisterStep?: number;

	getQuery: (parameter: string) => string;
	goToLogin: () => void;
	goToRegister: () => void;
	goToRegisterDone: () => void;
	goToSchoolHomepage: (schoolUrl: string, token: string, targetPath?: string) => void;
	goToSchoolWhereToHighFive: (schoolUrl: string, token: string) => void;
}

export const useRoutesUseCases: UseCases<undefined, Result> = () => {
	const location = useLocation();
	const navigate = useNavigate();

	const routes: Result["routes"] = {
		index: "/",
		login: "/aanmelden",
		// passwordReset URL is tied to the password reset mail.
		passwordReset: "/resetpassword",
		requestPasswordReset: "/wachtwoord-herstellen",
		register: {
			index: "/registreer",
			stepOne: "/registreer/1",
			stepTwo: "/registreer/2",
			stepThree: "/registreer/3",
			stepFour: "/registreer/4",
			stepFive: "/registreer/5",
			stepSix: "/registreer/6",
			stepSeven: "/registreer/7",
			done: "/registratie-voltooid",
		},
		forgotTagId: "/tag-id-vergeten",
	};

	const getQuery: Result["getQuery"] = (parameter) => new URLSearchParams(location.search).get(parameter) || "";

	const activeRegisterStep: Result["activeRegisterStep"] = location.pathname.includes(routes.register.index)
		? Number(location.pathname.match(/\d/))
		: undefined;

	const goToLogin: Result["goToLogin"] = () => {
		navigate(routes.login);
	};

	const goToRegister: Result["goToRegister"] = () => {
		navigate(routes.register.stepOne);
	};

	const goToRegisterDone: Result["goToRegisterDone"] = () => {
		navigate(routes.register.done);
	};

	const goToSchoolHomepage: Result["goToSchoolHomepage"] = (schoolUrl, token, targetPath) => {
		const path = targetPath || "mijnpagina";

		window.location.replace(`https://${schoolUrl}/${path}?jwt=${token}`);
	};

	const goToSchoolWhereToHighFive: Result["goToSchoolWhereToHighFive"] = (schoolUrl, token) => {
		window.location.replace(`https://${schoolUrl}/map?jwt=${token}`);
	};

	useEffect(() => {
		if (location) {
			event.emit("routeChanged", location.pathname);
		}
	}, [location]);

	return {
		routes,
		currentPathname: location.pathname,
		activeRegisterStep,

		getQuery,
		goToLogin,
		goToRegister,
		goToRegisterDone,
		goToSchoolHomepage,
		goToSchoolWhereToHighFive,
	};
};

export const useRoutesEffects = (routes: ReturnType<typeof useRoutesUseCases>) => {
	useEvent("registrationStarted", routes.goToRegister);
	useEvent("pupilRegistered", routes.goToRegisterDone);
	useEvent("loggedIn", routes.goToSchoolHomepage);
	useEvent("passwordReset", routes.goToLogin);
	useEvent("loginStarted", routes.goToLogin);
};
